import { Component, type ReactNode } from 'react';
import { isChunkLoadError } from '../lib/chunk-reload';
import { friendlyMessage } from '../lib/api-errors';

/** What the admin sees for a page that crashed or could not load. */
export function errorCopy(error: unknown): { title: string; body: string } {
  if (isChunkLoadError(error))
    return {
      title: 'A new version of the admin is available',
      body: 'This page could not load because the admin was updated. Reload to get the latest version.',
    };
  return {
    title: 'This page hit a problem',
    body: `${friendlyMessage(error).replace(/\.?$/, '.')} Try again, or reload the admin if it keeps happening.`,
  };
}

export function ErrorPanel({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { title, body } = errorCopy(error);
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
    >
      <p className="text-base font-semibold">{title}</p>
      <p>{body}</p>
      <div className="flex gap-2">
        {onRetry && !isChunkLoadError(error) ? (
          <button
            type="button"
            className="rounded-md border border-red-300 bg-white px-3 py-1.5 font-medium text-red-900 hover:bg-red-100"
            onClick={onRetry}
          >
            Try again
          </button>
        ) : null}
        <button
          type="button"
          className="rounded-md bg-red-700 px-3 py-1.5 font-medium text-white hover:bg-red-800"
          onClick={() => {
            window.location.reload();
          }}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

interface Props {
  children: ReactNode;
  /** Replaces the default panel (e.g. a full-screen version at the top level). */
  fallback?: (error: unknown, reset: () => void) => ReactNode;
}

/**
 * Catches a render error or a failed page chunk so the rest of the admin keeps working. Keyed by
 * route in Layout, so moving to another page clears it; "Try again" re-renders in place.
 */
export class ErrorBoundary extends Component<Props, { error: unknown }> {
  override state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  reset = () => {
    this.setState({ error: null });
  };

  override render() {
    const { error } = this.state;
    if (error === null) return this.props.children;
    return this.props.fallback ? (
      this.props.fallback(error, this.reset)
    ) : (
      <ErrorPanel error={error} onRetry={this.reset} />
    );
  }
}
